<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Handle, Position } from '@vue-flow/core';

const props = defineProps({
  data: { type: Object, required: true },
});

const COLORS = {
  message: {
    border: 'border-blue-400',
    badge: 'bg-blue-100 text-blue-700',
  },
  question: {
    border: 'border-purple-400',
    badge: 'bg-purple-100 text-purple-700',
  },
  decision: {
    border: 'border-yellow-400',
    badge: 'bg-yellow-100 text-yellow-700',
  },
  prayer: {
    border: 'border-pink-400',
    badge: 'bg-pink-100 text-pink-700',
  },
  end: {
    border: 'border-green-400',
    badge: 'bg-green-100 text-green-700',
  },
};

const { t } = useI18n();

const colors = computed(
  () => COLORS[props.data.step.step_type] || COLORS.message
);

const preview = computed(() => {
  const msg = props.data.step.message_text || '';
  return msg.length > 70 ? `${msg.slice(0, 70)}…` : msg;
});

const hasOptions = computed(() => props.data.step.options.length > 0);
const isEnd = computed(() => props.data.step.step_type === 'end');
</script>

<template>
  <div
    class="bg-white rounded-xl shadow-md border-2 w-56 select-none transition-shadow"
    :class="[
      colors.border,
      data.isSelected
        ? 'ring-2 ring-offset-1 ring-woot-400 shadow-lg'
        : 'hover:shadow-lg cursor-pointer',
    ]"
    @click="data.onSelect()"
  >
    <!-- Target handle (top) — all non-end nodes accept incoming edges -->
    <Handle
      v-if="!isEnd"
      type="target"
      :position="Position.Top"
      class="!w-3 !h-3 !bg-gray-400 !border-2 !border-white"
    />

    <!-- Header -->
    <div
      class="px-3 pt-3 pb-2 border-b border-gray-100 flex items-center gap-2"
    >
      <span
        class="shrink-0 w-5 h-5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold flex items-center justify-center"
      >
        {{ data.step.step_number }}
      </span>
      <span
        class="text-[10px] font-semibold px-2 py-0.5 rounded-full"
        :class="colors.badge"
      >
        {{ data.step.step_type }}
      </span>
      <span
        v-if="data.step.delay_seconds > 0"
        class="ml-auto text-[10px] text-gray-400 flex items-center gap-0.5"
      >
        <span class="i-lucide-clock size-3 shrink-0" />
        {{ data.step.delay_seconds }}{{ t('MINISTRY.FLOW_BUILDER.SEC') }}
      </span>
    </div>

    <!-- Message preview -->
    <div class="px-3 py-2 min-h-[40px]">
      <p
        class="text-[11px] leading-relaxed"
        :class="preview ? 'text-gray-600' : 'text-gray-300 italic'"
      >
        {{ preview || t('MINISTRY.FLOW_BUILDER.NO_MESSAGE') }}
      </p>
    </div>

    <!-- Option labels -->
    <div
      v-if="hasOptions"
      class="px-3 pb-3 pt-1 border-t border-gray-50 space-y-1"
    >
      <div
        v-for="(opt, i) in data.step.options"
        :key="opt.id ?? i"
        class="text-[10px] text-gray-400 flex items-center gap-1 truncate"
      >
        <span
          class="shrink-0 w-3.5 h-3.5 rounded-full bg-gray-100 text-gray-500 font-bold flex items-center justify-center text-[8px]"
        >
          {{ i + 1 }}
        </span>
        <span class="truncate">{{ opt.option_text || '(empty)' }}</span>
      </div>
    </div>

    <!-- Source handles — one per option, spaced across the bottom -->
    <Handle
      v-for="(opt, i) in data.step.options"
      :id="`opt-${opt.id ?? opt.option_number}`"
      :key="opt.id ?? opt.option_number"
      type="source"
      :position="Position.Bottom"
      class="!w-3 !h-3 !bg-woot-500 !border-2 !border-white"
      :style="{
        left: `${((i + 1) / (data.step.options.length + 1)) * 100}%`,
      }"
    />

    <!-- Single default source handle when no options (not end) -->
    <Handle
      v-if="!hasOptions && !isEnd"
      id="default-out"
      type="source"
      :position="Position.Bottom"
      class="!w-3 !h-3 !bg-gray-300 !border-2 !border-white"
    />
  </div>
</template>
